import { DocumentNode, useQuery } from '@apollo/client';
import { setAttributeColumns } from '../../utils/AttributeValues';
import { setLotAttributeColumns } from '../../utils/LotAttributeValues';
import { useMemo } from 'react';
import { ICustomColumn } from '../../utils/setColumnsFromArray';

type IAttributesForWorkspaceOptions = {
  valuesPath?: string;
  columnKey?: string;
  isAmendmentResult?: boolean;
  attributeNamePrefix?: string;
};

type IUseAttributesForWorkspace = (
  attributeQuery: DocumentNode,
  options?: IAttributesForWorkspaceOptions
) => { [key: string]: ICustomColumn } | undefined;

export const useAttributesForWorkspace: IUseAttributesForWorkspace = (
  attributeQuery,
  { valuesPath, columnKey, isAmendmentResult, attributeNamePrefix } = {}
) => {
  const { loading, data } = useQuery(attributeQuery, { fetchPolicy: 'cache-first' });

  return useMemo(() => {
    if (!loading && data?.list?.edges) {
      const columns = setAttributeColumns(data.list.edges, valuesPath, columnKey, isAmendmentResult);
      if (attributeNamePrefix) {
        for (const key in columns) {
          if (columns[key].name) {
            columns[key].name = `${attributeNamePrefix}${columns[key].name}`;
          }
        }
      }
      return columns;
    }
    return undefined;
  }, [loading, data, attributeNamePrefix]);
};

type ILotAttributesForWorkspaceOptions = {
  valuesPath?: string;
  columnKey?: string;
};

export const useLotAttributesForWorkspace = (
  lotAttributeQuery: DocumentNode,
  { valuesPath, columnKey }: ILotAttributesForWorkspaceOptions = {}
) => {
  const { loading, data } = useQuery(lotAttributeQuery, { fetchPolicy: 'cache-first' });

  return useMemo(() => {
    if (!loading && data?.list?.edges) {
      return setLotAttributeColumns(data.list.edges, valuesPath, columnKey);
    }
    return undefined;
  }, [loading, data]);
};

export const useAmendmentFieldNames = ({
  isAmendmentResult,
  amendmentRecord,
}: {
  isAmendmentResult?: boolean;
  amendmentRecord?: any;
}) => {
  let attributeFieldNames = [];
  if (isAmendmentResult && amendmentRecord) {
    attributeFieldNames = amendmentRecord.attributeValues.flatMap((attributeAmendmentRecord: any) => {
      if (!attributeAmendmentRecord.amendValue) return [];
      const columnIdentity = `attributeValues.attributeConfig.title.${attributeAmendmentRecord.attributeConfig.id}`;
      return [`${columnIdentity}.old`, columnIdentity];
    });
  }

  return attributeFieldNames;
};
